package org.moxultimate.topwithmox.techcrud.main

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.last
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.moxultimate.topwithmox.core.base.ViewModel
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.tech.api.TechRepository
import org.moxultimate.topwithmox.utils.toTitleCase
import org.moxultimate.topwithmox.tech.data.TechResponse
import org.moxultimate.topwithmox.techcrud.api.TechCRUDRepository
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

/**
 * TechCRUDViewModel
 * Manages cursor-based pagination, client-side type/search filtering,
 * and create/update/delete operations for tech entries.
 *
 * @param repository     Read-only data source for tech entries, tags, and types.
 * @param crudRepository Write repository for create, update, delete, saveTag, and saveType.
 */
class TechCRUDViewModel(
    private val repository: TechRepository,
    private val crudRepository: TechCRUDRepository,
) : ViewModel() {

    // ── Operation state (create / update / delete) ────────────────────────────

    private val _crudState = MutableStateFlow<TechCRUDState>(TechCRUDState.Idle)
    val crudState: StateFlow<TechCRUDState> = _crudState

    // ── List state ────────────────────────────────────────────────────────────

    private val _listState = MutableStateFlow<TechListState>(TechListState.Loading)
    val listState: StateFlow<TechListState> = _listState

    private val _isLoadingMore = MutableStateFlow(false)
    val isLoadingMore: StateFlow<Boolean> = _isLoadingMore

    private val _hasMoreInCurrentView = MutableStateFlow(true)
    val hasMoreInCurrentView: StateFlow<Boolean> = _hasMoreInCurrentView

    // ── Accumulated tech entries ───────────────────────────────────────────────

    private val accumulatedTechs = mutableListOf<TechResponse>()
    private var lastId: Long = Long.MAX_VALUE
    private var hasMore = true

    // ── Filter state ──────────────────────────────────────────────────────────

    private val _allTypes = MutableStateFlow<List<String>>(emptyList())
    val allTypes: StateFlow<List<String>> = _allTypes

    private val _allTags = MutableStateFlow<List<String>>(emptyList())
    val allTags: StateFlow<List<String>> = _allTags

    private val _selectedTypes = MutableStateFlow<Set<String>>(emptySet())
    val selectedTypes: StateFlow<Set<String>> = _selectedTypes

    private val _searchText = MutableStateFlow("")
    val searchText: StateFlow<String> = _searchText

    // ── List loading ──────────────────────────────────────────────────────────

    /**
     * loadTechs
     * Resets accumulated state and fetches the first page. Call from LaunchedEffect(Unit).
     */
    fun loadTechs() {
        accumulatedTechs.clear()
        lastId = Long.MAX_VALUE
        hasMore = true
        _selectedTypes.value = emptySet()
        _searchText.value = ""
        _hasMoreInCurrentView.value = true
        fetchPage(isInitialLoad = true)
    }

    /**
     * loadMoreIfNeeded
     * Called by the scroll handler when the user nears the bottom.
     * No-op when already loading or no more pages exist.
     */
    fun loadMoreIfNeeded() {
        if (hasMore && !_isLoadingMore.value) fetchPage(isInitialLoad = false)
    }

    private fun fetchPage(isInitialLoad: Boolean) {
        viewModelScope.launch {
            _isLoadingMore.value = true
            repository.getTechs(lastId).collect { state ->
                _listState.value = when (state) {
                    is APIState.Loading -> {
                        if (isInitialLoad) TechListState.Loading else _listState.value
                    }
                    is APIState.Error -> {
                        _isLoadingMore.value = false
                        TechListState.Error(state.error, state.statusCode)
                    }
                    is APIState.Success -> {
                        val page = state.data
                        _isLoadingMore.value = false
                        if (page.size < 20) hasMore = false
                        accumulatedTechs.addAll(page)
                        if (page.isNotEmpty()) lastId = page.last().id
                        _hasMoreInCurrentView.value = hasMore
                        buildListState()
                    }
                }
            }
        }
    }

    // ── Type / Tag loading ────────────────────────────────────────────────────

    /**
     * loadAllTypes
     * Fetches all distinct tech types. Call once on page load.
     */
    fun loadAllTypes() {
        viewModelScope.launch {
            repository.getAllTypes().collect { state ->
                if (state is APIState.Success) _allTypes.value = state.data.distinct().sorted()
            }
        }
    }

    /**
     * loadAllTags
     * Fetches all tech tags for search autocomplete. Call once on page load.
     */
    fun loadAllTags() {
        viewModelScope.launch {
            repository.getAllTags().collect { state ->
                if (state is APIState.Success) _allTags.value = state.data.distinct().sorted()
            }
        }
    }

    // ── Filtering ─────────────────────────────────────────────────────────────

    /**
     * toggleType
     * Adds or removes [type] from the active type filter and rebuilds state client-side.
     *
     * @param type Type string matching a value from [allTypes].
     */
    fun toggleType(type: String) {
        val current = _selectedTypes.value
        _selectedTypes.value = if (type in current) current - type else current + type
        _listState.value = buildListState()
    }

    /**
     * setSearchText
     * Updates the live search text and immediately re-filters the loaded tech list.
     *
     * @param text Text to search for in tech title and description.
     */
    fun setSearchText(text: String) {
        _searchText.value = text
        _listState.value = buildListState()
    }

    /**
     * clearAllFilters
     * Resets all active type chips and search text, restoring the full tech list.
     */
    fun clearAllFilters() {
        _selectedTypes.value = emptySet()
        _searchText.value = ""
        _listState.value = buildListState()
    }

    private fun buildListState(): TechListState.Success {
        val types = _selectedTypes.value
        val text = _searchText.value.trim()
        val result = accumulatedTechs.filter { tech ->
            val matchesType = types.isEmpty() ||
                    types.any { t -> tech.type?.equals(t.trim(), ignoreCase = true) == true }
            val matchesText = text.isBlank() ||
                    tech.title?.contains(text, ignoreCase = true) == true ||
                    tech.tags?.contains(text, ignoreCase = true) == true ||
                    tech.description?.contains(text, ignoreCase = true) == true
            matchesType && matchesText
        }
        return TechListState.Success(techs = result, hasMore = hasMore)
    }

    // ── CRUD operations ───────────────────────────────────────────────────────

    /**
     * createTech
     * Reads image file from [entry] then creates a new tech entry via POST /technology.
     * Also persists the type to its lookup table.
     *
     * @param entry Form data for the new tech entry (imageFile must be non-null).
     */
    fun createTech(entry: TechFormEntry) {
        viewModelScope.launch {
            _crudState.value = TechCRUDState.Loading
            try {
                val imageBytes = readFileAsBytes(entry.imageFile!!)
                when (val result = crudRepository.createTech(
                    title       = entry.title,
                    type        = entry.type,
                    tags        = entry.tags,
                    description = entry.description,
                    videoLink   = entry.videoLink,
                    imageBytes  = imageBytes,
                ).last()) {
                    is APIState.Error -> _crudState.value = TechCRUDState.Error(result.error, result.statusCode)
                    is APIState.Success -> {
                        persistType(entry.type)
                        persistTags(entry.tags)
                        _crudState.value = TechCRUDState.Success("Tech entry created successfully!")
                    }
                    else -> Unit
                }
            } catch (e: Throwable) {
                _crudState.value = TechCRUDState.Error(e.message ?: "Create failed")
            }
        }
    }

    /**
     * updateTech
     * Reads optional new image from [entry] then updates an existing tech entry via PUT /technology/{id}.
     *
     * @param id    Tech entry ID to update.
     * @param entry Updated form data (imageFile is optional for updates).
     */
    fun updateTech(id: Long, entry: TechFormEntry) {
        viewModelScope.launch {
            _crudState.value = TechCRUDState.Loading
            try {
                val imageBytes = entry.imageFile?.let { readFileAsBytes(it) }
                when (val result = crudRepository.updateTech(
                    id          = id,
                    title       = entry.title.takeIf { it.isNotBlank() },
                    type        = entry.type.takeIf { it.isNotBlank() },
                    tags        = entry.tags,
                    description = entry.description.takeIf { it.isNotBlank() },
                    videoLink   = entry.videoLink.takeIf { it.isNotBlank() },
                    imageBytes  = imageBytes,
                ).last()) {
                    is APIState.Error -> _crudState.value = TechCRUDState.Error(result.error, result.statusCode)
                    is APIState.Success -> {
                        persistType(entry.type)
                        persistTags(entry.tags)
                        _crudState.value = TechCRUDState.Success("Tech entry updated successfully!")
                    }
                    else -> Unit
                }
            } catch (e: Throwable) {
                _crudState.value = TechCRUDState.Error(e.message ?: "Update failed")
            }
        }
    }

    /**
     * deleteTech
     * Deletes a tech entry and its S3 files via DELETE /technology/{id}.
     *
     * @param id Tech entry ID to delete.
     */
    fun deleteTech(id: Long) {
        viewModelScope.launch {
            _crudState.value = TechCRUDState.Loading
            try {
                when (val result = crudRepository.deleteTechById(id).last()) {
                    is APIState.Error -> _crudState.value = TechCRUDState.Error(result.error, result.statusCode)
                    else -> _crudState.value = TechCRUDState.Success("Tech entry deleted successfully!")
                }
            } catch (e: Throwable) {
                _crudState.value = TechCRUDState.Error(e.message ?: "Delete failed")
            }
        }
    }

    /**
     * resetState
     * Resets the CRUD operation state back to Idle.
     */
    fun resetState() {
        _crudState.value = TechCRUDState.Idle
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    private fun persistType(type: String) {
        viewModelScope.launch {
            val titleCased = type.trim().toTitleCase()
            if (titleCased.isNotBlank()) crudRepository.saveType(titleCased).collect {}
        }
    }

    /**
     * persistTags
     * Splits the pipe-separated [tags] string, Title-Cases each entry, and fires a
     * best-effort POST /technology-tag for every unique non-blank tag.
     * Errors are silently discarded — lookup table updates never block the main flow.
     *
     * @param tags Pipe-separated tag string from the form.
     */
    private fun persistTags(tags: String) {
        tags.split("|")
            .map { it.trim().toTitleCase() }
            .filter { it.isNotBlank() }
            .distinct()
            .forEach { tag ->
                viewModelScope.launch {
                    try { crudRepository.saveTag(tag).collect {} } catch (_: Throwable) {}
                }
            }
    }

    /**
     * readFileAsBytes
     * Reads a browser File object as a ByteArray using the FileReader API.
     * Suspends until the read is complete or throws on failure.
     *
     * @param file The browser File to read.
     * @return ByteArray of the file's contents.
     */
    private suspend fun readFileAsBytes(file: File): ByteArray = suspendCancellableCoroutine { cont ->
        val reader = FileReader()
        reader.onload = {
            val buffer = (reader.result as ArrayBuffer)
            val bytes = Int8Array(buffer).unsafeCast<ByteArray>()
            cont.resume(bytes)
        }
        reader.onerror = {
            cont.resumeWithException(Exception("Failed to read file: ${file.name}"))
        }
        reader.readAsArrayBuffer(file)
    }
}

/**
 * TechFormEntry
 * UI-layer form state for a single tech entry (add or edit).
 * [imageFile] is null when no new file has been selected.
 */
data class TechFormEntry(
    val imageFile: File? = null,
    val title: String = "",
    val type: String = "",
    val tags: String = "",
    val description: String = "",
    val videoLink: String = "",
) {
    /** True when all required fields and the image file are present (used for add flow). */
    val isValid: Boolean
        get() = imageFile != null && title.isNotBlank() && type.isNotBlank()

    /** True when required text fields are filled (used for update flow — image is optional). */
    val isValidForUpdate: Boolean
        get() = title.isNotBlank() && type.isNotBlank()

    /** Names of required fields that are still empty or missing — used to show validation hints. */
    val missingFields: List<String>
        get() = buildList {
            if (imageFile == null) add("Image")
            if (title.isBlank()) add("Title")
            if (type.isBlank()) add("Type")
        }
}

/**
 * toFormEntry
 * Convenience extension to pre-populate a [TechFormEntry] from a [TechResponse].
 */
fun TechResponse.toFormEntry() = TechFormEntry(
    title       = title.orEmpty(),
    type        = type.orEmpty(),
    tags        = tags.orEmpty(),
    description = description.orEmpty(),
    videoLink   = videoLink.orEmpty(),
)
